import React, { useState, useEffect, useCallback, useRef } from "react";
import Avatar from "./Avatar";
import Icon from "./Icon";
import { FILE_UPLOADS_ENABLED, UPLOADS_DISABLED_MESSAGE } from "../../config/features";
import * as activityService from "../../services/activityService";
import { resolveUploadUrl } from "../../utils/apiClient";
import styles from "./ActivityPanel.module.css";

function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay === 1) return "Yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  return date.toLocaleDateString();
}

function fieldLabel(fieldName) {
  return (fieldName || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function ActivityItem({ item }) {
  const actorName = item.actor_name || "Someone";

  let body;
  if (item.activity_type === "comment") {
    body = (
      <>
        <div className={styles.itemText}>
          <strong>{actorName}</strong> added a comment:
        </div>
        <div className={styles.commentBubble}>{item.comment_text}</div>
      </>
    );
  } else if (item.activity_type === "file_upload") {
    body = (
      <div className={styles.itemText}>
        <strong>{actorName}</strong> attached a file:
        <div>
          {item.file_url ? (
            <a
              className={styles.fileLink}
              href={resolveUploadUrl(item.file_url)}
              target="_blank"
              rel="noreferrer"
            >
              <Icon name="file" size="0.85rem" />
              {item.file_name}
            </a>
          ) : (
            <span className={styles.fileLink}>
              <Icon name="file" size="0.85rem" />
              {item.file_name} (removed)
            </span>
          )}
        </div>
      </div>
    );
  } else if (item.activity_type === "file_delete") {
    body = (
      <div className={styles.itemText}>
        <strong>{actorName}</strong> removed a file: {item.file_name}
      </div>
    );
  } else {
    // field_change
    body = (
      <div className={styles.itemText}>
        <strong>{actorName}</strong> updated {fieldLabel(item.field_name)}
        {(item.old_value || item.new_value) && (
          <div className={styles.fieldChange}>
            <span className={styles.oldValue}>{item.old_value || "—"}</span>
            <span className={styles.arrow}><Icon name="arrowRight" size="0.8rem" /></span>
            <span className={styles.newValue}>{item.new_value || "—"}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.item}>
      <Avatar name={actorName} size="sm" />
      <div className={styles.itemBody}>
        {body}
        <div className={styles.timestamp}>{formatRelativeTime(item.created_at)}</div>
      </div>
    </div>
  );
}

/**
 * ActivityPanel - generic right-column feed of field changes, comments and
 * file uploads for an entity, plus a comment/attach box. Not specific to
 * projects/leads: pass any entityType with a registered base path in
 * services/activityService.js.
 */
export default function ActivityPanel({ entityType, entityId, canComment = true, onFileUploaded = null, refreshSignal = 0 }) {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const feedRef = useRef(null);

  const load = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      // The API returns newest-first (for cursor pagination); flip it here
      // so the feed itself reads top-to-bottom like a chat thread, oldest
      // to newest, with the latest activity always at the bottom.
      const rows = await activityService.getActivity(entityType, entityId);
      setActivity([...rows].reverse());
    } catch (err) {
      setError("Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    load();
    // refreshSignal lets the parent force a reload after a field save
    // elsewhere in the modal, without this component knowing about it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, refreshSignal]);

  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [activity]);

  const handleSendComment = async () => {
    if (!commentText.trim() || posting) return;
    setPosting(true);
    setError(null);
    try {
      const newItem = await activityService.addComment(entityType, entityId, commentText.trim());
      setActivity((prev) => [...prev, newItem]);
      setCommentText("");
    } catch (err) {
      setError("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPosting(true);
    setError(null);
    try {
      await activityService.uploadFile(entityType, entityId, file);
      await load();
      onFileUploaded?.();
    } catch (err) {
      setError("Failed to upload file");
    } finally {
      setPosting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Icon name="clock" size="0.9rem" className={styles.headerIcon} />
        Activity &amp; comments
      </div>
      <div className={styles.feed} ref={feedRef}>
        {loading ? (
          <div className={styles.emptyState}>Loading…</div>
        ) : activity.length === 0 ? (
          <div className={styles.emptyState}>No activity yet.</div>
        ) : (
          activity.map((item) => <ActivityItem key={item.activity_id} item={item} />)
        )}
      </div>
      {canComment && (
        <div className={styles.composer}>
          {error && <div className={styles.composerError}>{error}</div>}
          <textarea
            className={styles.commentInput}
            placeholder={
              FILE_UPLOADS_ENABLED
                ? "Add a comment or attach a file..."
                : "Add a comment..."
            }
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            disabled={posting}
            rows={2}
          />
          <div className={styles.composerActions}>
            <button
              type="button"
              className={styles.attachButton}
              onClick={() => fileInputRef.current?.click()}
              disabled={posting || !FILE_UPLOADS_ENABLED}
              title={FILE_UPLOADS_ENABLED ? "Attach a file" : UPLOADS_DISABLED_MESSAGE}
              aria-label="Attach a file"
            >
              <Icon name="paperclip" size="1rem" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className={styles.hiddenFileInput}
              onChange={handleFileChange}
            />
            <button
              type="button"
              className={styles.sendButton}
              onClick={handleSendComment}
              disabled={posting || !commentText.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
